import { useState } from 'react';
import { Check } from 'lucide-react';
import {
  describeDueAfter,
  planLengthDays,
  type DeliveryMilestone,
  type QuoteBlock,
  type QuotePlanOption, type BlockHoursStart } from '@t3/shared';
import { Emphasis, QuoteListView } from './QuoteBlockEditor.tsx';

// The quote as the CLIENT sees it.
//
// One component, used by two callers: the public page a client opens to
// sign, and the Preview button in the editor. That is the whole point —
// a preview rendered by its own code is a preview that eventually lies,
// and the one thing a preview must never do is show something different
// from what gets sent.
//
// IT REPRODUCES THE QWILR PAGES CLIENTS HAVE BEEN SIGNING. Same bands,
// same type, same plan cards, same accept block - measured off the
// published block-hours page (computed styles, not eyeballed), because
// replacing Qwilr should not change what a client recognises. That is
// why this page uses Qwilr's purple (#54387E) and Poppins/Inter rather
// than the app's own tokens: it is the house quote, not an app screen.
//
// The layout is driven by the document, not by which template it came
// from:
//   * sections before the first rolled-up one are bands of their own;
//   * the plans (or the priced table) render inside the section marked
//     `showsPricing`, or in a band of their own when none is;
//   * the rolled-up sections are the agreements, and sit in the dark
//     "Let's get started" band with the closing text and Accept;
//   * anything after them follows the signature, as on the page.
//
// It takes a plain shape rather than a snapshot or a document, because
// the editor's unsaved draft is neither.

export interface QuoteViewData {
  title: string;
  intro: string | null;
  closing: string | null;
  organizationName: string;
  preparedByAgentName: string | null;
  sections: Array<{
    heading: string;
    blocks?: QuoteBlock[];
    body?: string;
    collapsible?: boolean;
    showsPricing?: boolean;
  }>;
  lineItems: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitPriceUsd: number;
    amountUsd: number;
  }>;
  totalAmountUsd: number;
  /** Plans the client chooses between. Empty on a single-price quote. */
  planOptions?: QuotePlanOption[];
  /** The timeline: milestones due N days after signing, with their tasks. */
  deliveryPlan?: DeliveryMilestone[];
  /** Taken at signature. Absent or 0 means the work is invoiced. */
  depositCents?: number;
  balanceCents?: number;
  /** A plan quote that saves a card at signing (Stripe configured). */
  cardRequired?: boolean;
  /** `YYYY-MM-DD`: the 1st the first monthly charge lands on. */
  firstChargeOn?: string | null;
  /** `YYYY-MM-DD`, the 1st of this month, when the client may start now. */
  startNowOn?: string | null;
}

/** `YYYY-MM-DD` as "November 1, 2026", without a timezone shifting the day. */
const longDate = (iso: string): string =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

type Section = QuoteViewData['sections'][number];

interface ViewProps {
  quote: QuoteViewData;
  /** The chosen plan id. */
  selectedPlanId?: string | null;
  /** Present only where the client may actually choose. */
  onSelectPlan?: (planId: string | null) => void;
  /** What Accept does. Absent: the button is drawn but inert (preview). */
  onAccept?: () => void;
  /** Rendered in place of the Accept button - e.g. the signed confirmation. */
  acceptSlot?: React.ReactNode;
  /** Shown beside the total when Accept was pressed with no plan chosen. */
  planPrompt?: boolean;
  /** Block hours: when the client chose to start. */
  start?: BlockHoursStart | null;
  /** Present only where the client may choose when to start. */
  onStartChange?: (start: BlockHoursStart) => void;
}

// Measured off the published page.
const PURPLE = '#54387E';
const DARK = '#333333';
const LIGHT = '#F2F6F7';
const HEADING_ON_COLOR = '#F2F2F2';
const SLATE = '#47535D';
const TOTAL_GREY = '#E0E8EC';
const HAIRLINE = 'rgba(129,162,178,0.25)';
const POPPINS = '"Poppins", "Inter", system-ui, sans-serif';
const INTER = '"Inter", system-ui, sans-serif';

const money = (value: number): string =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** Snapshots frozen before the block format carry only plain-text bodies. */
function blocksOf(section: Section): QuoteBlock[] {
  if (section.blocks && section.blocks.length > 0) return section.blocks;
  return section.body ? [{ type: 'paragraph', text: section.body }] : [];
}

export function QuoteDocumentView({
  quote,
  selectedPlanId,
  onSelectPlan,
  onAccept,
  acceptSlot,
  planPrompt,
  start,
  onStartChange,
}: ViewProps): React.ReactElement {
  const plans = quote.planOptions ?? [];
  const chosen = plans.find((p) => p.id === selectedPlanId) ?? null;

  const firstRolled = quote.sections.findIndex((s) => s.collapsible);
  const before = firstRolled === -1 ? quote.sections : quote.sections.slice(0, firstRolled);
  const agreements = quote.sections.filter((s) => s.collapsible);
  const after = firstRolled === -1 ? [] : quote.sections.slice(firstRolled).filter((s) => !s.collapsible);
  const pricingHasHome = quote.sections.some((s) => s.showsPricing && !s.collapsible);

  // The running figure in the bar: the chosen plan, or the quote's own
  // total. Never $0.00 for an unchosen ladder - that reads as "free".
  const barAmount = plans.length > 0 ? (chosen ? chosen.monthlyPriceUsd : null) : quote.totalAmountUsd;

  const pricing = (
    <Pricing
      quote={quote}
      plans={plans}
      chosen={chosen}
      selectedPlanId={selectedPlanId ?? null}
      onSelectPlan={onSelectPlan}
      planPrompt={planPrompt}
      start={start ?? null}
      onStartChange={onStartChange}
    />
  );

  return (
    <div className="bg-white text-left" style={{ fontFamily: INTER }}>
      {/* The bar. Title, the running total, and Accept, always in reach. */}
      <div
        className="sticky top-0 z-30 flex h-[62px] items-center justify-between gap-4 px-4 sm:px-[60px]"
        style={{ backgroundColor: PURPLE }}
      >
        <p className="min-w-0 truncate text-[14px] font-bold text-white">{quote.title}</p>
        <div className="flex shrink-0 items-center gap-2">
          {barAmount !== null && (
            <span className="px-3 text-[15px] font-medium tabular-nums text-white">
              {money(barAmount)}
            </span>
          )}
          {!acceptSlot && (
            <button
              type="button"
              onClick={onAccept}
              disabled={!onAccept}
              className="h-10 rounded-[4px] px-4 text-[15px] font-medium text-white transition hover:bg-white/10 disabled:cursor-default disabled:hover:bg-transparent"
              style={{ backgroundColor: PURPLE }}
            >
              Accept
            </button>
          )}
        </div>
      </div>

      {/* Cover. */}
      <header
        className="flex h-[240px] items-center justify-center px-6 sm:h-[360px]"
        style={{ backgroundColor: DARK }}
      >
        <img
          src="/Original-Top-White-Text8k22.png"
          alt="T3 Tech Partners - Get. More. Done."
          className="w-[300px] max-w-full sm:w-[474px]"
        />
      </header>

      {quote.intro && (
        <Band tone="light">
          <TextBody blocks={[{ type: 'paragraph', text: quote.intro }]} tone="light" />
        </Band>
      )}

      {before.map((section, i) =>
        section.showsPricing ? (
          <PricingBand key={`b${i}`} heading={section.heading} blocks={blocksOf(section)}>
            {pricing}
          </PricingBand>
        ) : (
          <TextBand key={`b${i}`} section={section} tone="light" />
        ),
      )}

      {!pricingHasHome && (
        <PricingBand heading={plans.length > 0 ? 'Choose your plan' : 'Investment'} blocks={[]}>
          {pricing}
        </PricingBand>
      )}

      {(quote.deliveryPlan ?? []).length > 0 && <DeliveryPlanBand plan={quote.deliveryPlan!} />}

      <AcceptBand
        agreements={agreements}
        closing={quote.closing}
        onAccept={onAccept}
        acceptSlot={acceptSlot}
      />

      {after.map((section, i) =>
        section.showsPricing ? (
          <PricingBand key={`a${i}`} heading={section.heading} blocks={blocksOf(section)}>
            {pricing}
          </PricingBand>
        ) : (
          <TextBand key={`a${i}`} section={section} tone={i % 2 === 0 ? 'purple' : 'light'} />
        ),
      )}
    </div>
  );
}

// --- bands -----------------------------------------------------------------

function Band({
  tone,
  id,
  children,
  narrow,
}: {
  tone: 'light' | 'purple' | 'dark';
  id?: string;
  children: React.ReactNode;
  narrow?: boolean;
}): React.ReactElement {
  const bg = tone === 'light' ? LIGHT : tone === 'purple' ? PURPLE : DARK;
  return (
    <section
      id={id}
      className={tone === 'dark' ? 'px-4 py-14 sm:py-[72px]' : 'px-4 py-16 sm:py-[108px]'}
      style={{ backgroundColor: bg, boxShadow: '0 1px 0 rgba(129,162,178,0.2), 0 -1px 0 rgba(129,162,178,0.2)' }}
    >
      <div className={`mx-auto w-full ${narrow ? 'max-w-[805px]' : 'max-w-[976px]'}`}>{children}</div>
    </section>
  );
}

function TextBand({ section, tone }: { section: Section; tone: 'light' | 'purple' }): React.ReactElement {
  const blocks = blocksOf(section);
  const onPurple = tone === 'purple';
  return (
    <Band tone={tone} narrow={onPurple}>
      <h2
        className={`font-bold leading-[1.2] ${onPurple ? 'text-center text-[34px] sm:text-[50px]' : 'text-[32px] sm:text-[46px]'}`}
        style={{ fontFamily: POPPINS, color: onPurple ? HEADING_ON_COLOR : PURPLE, paddingBottom: '0.2em' }}
      >
        {section.heading}
      </h2>
      <TextBody blocks={blocks} tone={tone} />
    </Band>
  );
}

/** Blocks in a band's typography. */
function TextBody({
  blocks,
  tone,
  center,
}: {
  blocks: QuoteBlock[];
  tone: 'light' | 'purple';
  center?: boolean;
}): React.ReactElement {
  const color = tone === 'light' ? PURPLE : '#FFFFFF';
  return (
    <div style={{ color }} className={center ? 'text-center' : ''}>
      {blocks.map((block, i) => {
        if (block.type === 'paragraph') {
          return (
            <p key={i} className="whitespace-pre-line pb-5 text-[18px] leading-[1.5] sm:text-[20px]">
              <Emphasis text={block.text} />
            </p>
          );
        }
        if (block.type === 'subheading') {
          return (
            <h3 key={i} className="pb-3 pt-2 text-[22px] font-bold leading-snug sm:text-[24px]">
              <Emphasis text={block.text} />
            </h3>
          );
        }
        return (
          <QuoteListView
            key={i}
            block={block}
            className={`pb-5 text-[16px] leading-[1.5] ${center ? 'mx-auto inline-block text-left' : ''}`}
            itemClassName="mb-[5px]"
          />
        );
      })}
    </div>
  );
}

function PricingBand({
  heading,
  blocks,
  children,
}: {
  heading: string;
  blocks: QuoteBlock[];
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Band tone="purple" id="pricing">
      <h2
        className="text-center text-[38px] font-bold leading-[1.2] sm:text-[59px]"
        style={{ fontFamily: POPPINS, color: HEADING_ON_COLOR, paddingBottom: '0.6em' }}
      >
        {heading}
      </h2>
      <div className="mx-auto max-w-[640px]">
        <TextBody blocks={blocks} tone="purple" center />
      </div>
      {children}
    </Band>
  );
}

// --- pricing ---------------------------------------------------------------

function Pricing({
  quote,
  plans,
  chosen,
  selectedPlanId,
  onSelectPlan,
  planPrompt,
  start,
  onStartChange,
}: {
  quote: QuoteViewData;
  start: BlockHoursStart | null;
  onStartChange?: (start: BlockHoursStart) => void;
  plans: QuotePlanOption[];
  chosen: QuotePlanOption | null;
  selectedPlanId: string | null;
  onSelectPlan?: (planId: string | null) => void;
  planPrompt?: boolean;
}): React.ReactElement {
  // Block hours started now are charged today; otherwise only a deposit is.
  const startChoice = Boolean(quote.startNowOn && onStartChange && plans.length > 0);
  const dueToday = chosen && start === 'now' ? chosen.monthlyPriceUsd : (quote.depositCents ?? 0) / 100;
  return (
    <div className="mt-4">
      {plans.length > 0 ? (
        <PlanCards plans={plans} selectedPlanId={selectedPlanId} onSelectPlan={onSelectPlan} />
      ) : (
        <LineItemTable lineItems={quote.lineItems} />
      )}

      <div className="mt-12 flex justify-center px-0 sm:mt-[72px]">
        <div
          className="w-full max-w-[400px] rounded-[8px] p-6"
          style={{ backgroundColor: TOTAL_GREY, color: SLATE }}
          aria-live="polite"
        >
          {plans.length > 0 ? (
            chosen ? (
              <>
                {startChoice && (
                  <StartChoice
                    startNowOn={quote.startNowOn!}
                    firstChargeOn={quote.firstChargeOn ?? null}
                    price={chosen.monthlyPriceUsd}
                    start={start}
                    onChange={onStartChange!}
                    prompt={Boolean(planPrompt) && !start}
                  />
                )}
                <TotalRow label="Total Recurring" value={`${money(chosen.monthlyPriceUsd)} / month`} />
                <div className="my-2 border-t" style={{ borderColor: 'rgba(71,83,93,0.2)' }} />
                {/* What signing actually costs today. Block hours are
                    invoiced monthly and a plan quote takes no deposit, so
                    this is honestly $0 - not the month's fee, which is
                    what the Qwilr page showed and did not charge. */}
                {startChoice && !start ? (
                  // An honest blank: what is due today depends on the choice.
                  <div className="flex items-center justify-between gap-4 py-2">
                    <span className="text-[20px] font-bold">Total Due Today</span>
                    <span className="text-right text-[16px]">Choose when to start</span>
                  </div>
                ) : (
                  <TotalRow label="Total Due Today" value={money(dueToday)} grand />
                )}
                {/* $0 today is not "no card": the card is saved as part
                    of signing, and the client should know before Accept. */}
                {quote.cardRequired && (
                  <p className="mt-2 text-[14px] leading-snug">
                    {start === 'now' ? (
                      <>
                        <span className="font-semibold">Your card is charged {money(chosen.monthlyPriceUsd)} when you sign</span>{' '}
                        for this month, then on the 1st of each month from{' '}
                        {quote.firstChargeOn ? longDate(quote.firstChargeOn) : 'next month'}.
                      </>
                    ) : (
                      <>
                        <span className="font-semibold">A card is saved when you sign.</span> It is first charged{' '}
                        {money(chosen.monthlyPriceUsd)} on{' '}
                        {quote.firstChargeOn ? longDate(quote.firstChargeOn) : 'the 1st of next month'}, then on the 1st
                        of each month.
                      </>
                    )}
                  </p>
                )}
              </>
            ) : (
              // An honest blank, not $0.00: nothing has been chosen yet.
              <div className="flex items-center justify-between gap-4 py-2">
                <span className="text-[20px] font-bold">Total</span>
                <span className={`text-right text-[16px] ${planPrompt ? 'font-semibold text-[#B42318]' : ''}`}>
                  {planPrompt ? 'Choose a plan to continue' : 'Select a plan above'}
                </span>
              </div>
            )
          ) : (
            <>
              <TotalRow label="Total" value={money(quote.totalAmountUsd)} grand />
              {/* The payment terms belong next to the total: a client should
                  know what signing costs before being asked for a card. */}
              {dueToday > 0 && (
                <>
                  <div className="my-2 border-t" style={{ borderColor: 'rgba(71,83,93,0.2)' }} />
                  <TotalRow label="Due on acceptance" value={money(dueToday)} />
                  {(quote.balanceCents ?? 0) > 0 && (
                    <TotalRow label="Due on completion" value={money((quote.balanceCents ?? 0) / 100)} />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Start now or on the 1st - offered in the first two weeks of a month. */
function StartChoice({
  startNowOn,
  firstChargeOn,
  price,
  start,
  onChange,
  prompt,
}: {
  startNowOn: string;
  firstChargeOn: string | null;
  price: number;
  start: BlockHoursStart | null;
  onChange: (start: BlockHoursStart) => void;
  prompt: boolean;
}): React.ReactElement {
  const thisMonth = new Date(`${startNowOn}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
  const options: Array<{ value: BlockHoursStart; label: string; detail: string }> = [
    {
      value: 'now',
      label: 'Start now',
      detail: `Your ${thisMonth} hours are available right away. ${money(price)} is charged today.`,
    },
    {
      value: 'next_month',
      label: firstChargeOn ? `Start on ${longDate(firstChargeOn)}` : 'Start on the 1st',
      detail: `Nothing is charged today. ${money(price)} is charged on the 1st.`,
    },
  ];
  return (
    <fieldset className="mb-3">
      <legend className={`mb-2 text-[16px] font-bold ${prompt ? 'text-[#B42318]' : ''}`}>
        {prompt ? 'Choose when to start' : 'When would you like to start?'}
      </legend>
      <div className="grid gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            aria-pressed={start === o.value}
            className={`rounded-[6px] border-2 p-3 text-left transition ${
              start === o.value ? 'border-[#54387E] bg-white' : 'border-transparent bg-white/60 hover:border-[#54387E]/40'
            }`}
          >
            <span className="block text-[15px] font-semibold">{o.label}</span>
            <span className="mt-0.5 block text-[13px] leading-snug">{o.detail}</span>
          </button>
        ))}
      </div>
      <div className="my-3 border-t" style={{ borderColor: 'rgba(71,83,93,0.2)' }} />
    </fieldset>
  );
}

function TotalRow({ label, value, grand }: { label: string; value: string; grand?: boolean }): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <span className={grand ? 'text-[20px] font-bold' : 'text-[16px]'}>{label}</span>
      <span className="text-right text-[16px] tabular-nums">{value}</span>
    </div>
  );
}

/**
 * The plan ladder, as Qwilr draws it: a white card per plan with the
 * name, the price and a Select button, the inclusions underneath on the
 * band, and the chosen one standing forward while the rest step back.
 *
 * `onSelectPlan` is absent on a signed quote; the cards then show the
 * plan that was signed for and no controls.
 */
function PlanCards({
  plans,
  selectedPlanId,
  onSelectPlan,
}: {
  plans: QuotePlanOption[];
  selectedPlanId: string | null;
  onSelectPlan?: (planId: string | null) => void;
}): React.ReactElement {
  const anyChosen = plans.some((p) => p.id === selectedPlanId);
  return (
    <div className="flex flex-col items-center gap-8 sm:flex-row sm:flex-wrap sm:items-start sm:justify-center sm:gap-6">
      {plans.map((plan) => {
        const isChosen = plan.id === selectedPlanId;
        const recede = anyChosen && !isChosen;
        return (
          <div
            key={plan.id}
            className="w-full max-w-[370px] transition-transform duration-200 sm:w-[209px]"
            style={{ transform: recede ? 'scale(0.9)' : undefined, transformOrigin: 'center 40%' }}
          >
            <div
              className="rounded-[8px] bg-white p-6 text-center"
              style={{ border: `1px solid ${HAIRLINE}`, color: SLATE }}
            >
              <h3 className="text-[20px] font-bold leading-[1.3]" style={{ fontFamily: POPPINS }}>
                {plan.name}
              </h3>
              <p className="mt-4 text-[30px] leading-none tabular-nums">{money(plan.monthlyPriceUsd)}</p>
              <p className="mt-2 text-[16px]">/ month</p>
              <div className="mt-6 h-[42px]">
                {onSelectPlan ? (
                  <button
                    type="button"
                    onClick={() => onSelectPlan(isChosen ? null : plan.id)}
                    aria-pressed={isChosen}
                    className="inline-flex h-full w-full items-center justify-center gap-2 rounded-[6px] text-[14px] font-semibold transition"
                    style={
                      isChosen
                        ? { backgroundColor: DARK, color: '#FFFFFF', boxShadow: `0 0 0 2px ${HAIRLINE}` }
                        : { backgroundColor: '#FFFFFF', color: '#242C39', boxShadow: `0 0 0 1px ${HAIRLINE}` }
                    }
                  >
                    {isChosen && <Check className="h-4 w-4" strokeWidth={3} aria-hidden="true" />}
                    {isChosen ? 'Selected' : 'Select'}
                  </button>
                ) : (
                  isChosen && (
                    <span
                      className="inline-flex h-full w-full items-center justify-center gap-2 rounded-[6px] text-[14px] font-semibold text-white"
                      style={{ backgroundColor: DARK }}
                    >
                      <Check className="h-4 w-4" strokeWidth={3} aria-hidden="true" /> Selected
                    </span>
                  )
                )}
              </div>
            </div>
            {plan.inclusions.length > 0 && (
              <ul className="mt-4 space-y-[9px] px-1 text-left">
                {plan.inclusions.map((item, i) => (
                  <li key={i} className="flex gap-3 text-[14px] leading-[1.4] text-white">
                    <Check
                      className="mt-[2px] h-4 w-4 shrink-0"
                      strokeWidth={2.5}
                      style={{ color: 'rgba(40,32,52,0.75)' }}
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** A single-price quote's lines, on a white card in the pricing band. */
function LineItemTable({ lineItems }: { lineItems: QuoteViewData['lineItems'] }): React.ReactElement {
  return (
    <div
      className="mx-auto max-w-[760px] overflow-hidden rounded-[8px] bg-white"
      style={{ border: `1px solid ${HAIRLINE}`, color: SLATE }}
    >
      {lineItems.map((item, i) => (
        <div
          key={i}
          className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 px-6 py-5"
          style={i > 0 ? { borderTop: `1px solid ${HAIRLINE}` } : undefined}
        >
          <div className="min-w-0 flex-1">
            <p className="text-[18px] font-semibold">{item.description}</p>
            <p className="mt-1 text-[14px] tabular-nums">
              {item.quantity} {item.unit} × {money(item.unitPriceUsd)}
            </p>
          </div>
          <p className="text-[18px] tabular-nums">{money(item.amountUsd)}</p>
        </div>
      ))}
    </div>
  );
}

// --- the delivery plan -----------------------------------------------------

/**
 * The timeline the client is signing: each milestone, when it lands
 * relative to signing, and what it is made of. Relative, not dated - the
 * quote does not know when it will be signed. Once it is, these become the
 * dated milestones and the checklist on the client's project page.
 */
function DeliveryPlanBand({ plan }: { plan: DeliveryMilestone[] }): React.ReactElement {
  const length = planLengthDays(plan);
  return (
    <Band tone="light" id="delivery-plan">
      <h2
        className="text-[32px] font-bold leading-[1.2] sm:text-[46px]"
        style={{ fontFamily: POPPINS, color: PURPLE, paddingBottom: '0.2em' }}
      >
        Delivery plan
      </h2>
      <p className="pb-6 text-[18px] leading-[1.5] sm:text-[20px]" style={{ color: PURPLE }}>
        How the work will run, from the day you sign.
        {length !== null && length > 0 && (
          <>
            {' '}
            Target completion: <strong>{describeDueAfter(length)}</strong>.
          </>
        )}
      </p>
      <ol className="relative">
        {plan.map((milestone, i) => (
          <li key={milestone.id} className="relative flex gap-5 pb-8 last:pb-0">
            {/* The rail and its stop. */}
            {i < plan.length - 1 && (
              <span
                className="absolute left-[11px] top-7 h-[calc(100%-1.25rem)] w-[2px]"
                style={{ backgroundColor: 'rgba(84,56,126,0.25)' }}
                aria-hidden="true"
              />
            )}
            <span
              className="relative z-10 mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-white"
              style={{ backgroundColor: PURPLE }}
              aria-hidden="true"
            >
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h3 className="text-[20px] font-bold leading-snug sm:text-[22px]" style={{ color: PURPLE }}>
                  {milestone.title}
                </h3>
                <span
                  className="rounded-full bg-white px-3 py-0.5 text-[14px] font-semibold"
                  style={{ color: SLATE, border: `1px solid ${HAIRLINE}` }}
                >
                  {describeDueAfter(milestone.dueAfterDays)}
                </span>
              </div>
              {milestone.tasks.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {milestone.tasks.map((task) => (
                    <li key={task.id} className="flex gap-3 text-[17px] leading-[1.45]" style={{ color: SLATE }}>
                      <Check
                        className="mt-[3px] h-4 w-4 shrink-0"
                        strokeWidth={2.5}
                        style={{ color: PURPLE }}
                        aria-hidden="true"
                      />
                      <span>{task.title}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ol>
    </Band>
  );
}

// --- the accept block ------------------------------------------------------

function AcceptBand({
  agreements,
  closing,
  onAccept,
  acceptSlot,
}: {
  agreements: Section[];
  closing: string | null;
  onAccept?: () => void;
  acceptSlot?: React.ReactNode;
}): React.ReactElement {
  return (
    <Band tone="dark" id="accept">
      <h2
        className="text-center text-[40px] font-bold leading-[1.3] sm:text-[60px]"
        style={{ fontFamily: POPPINS, color: HEADING_ON_COLOR, paddingBottom: '0.4em' }}
      >
        Let&apos;s get started
      </h2>
      {agreements.length > 0 && (
        <div className="space-y-[22px]">
          {agreements.map((section, i) => (
            <Agreement key={i} section={section} />
          ))}
        </div>
      )}
      {closing && (
        <p className="mt-5 whitespace-pre-line px-2 text-center text-[18px] leading-[1.5] text-white sm:px-6 sm:text-[20px]">
          <Emphasis text={closing} />
        </p>
      )}
      <div className="mt-14 flex justify-center sm:mt-[72px]">
        {acceptSlot ?? (
          <button
            type="button"
            onClick={onAccept}
            disabled={!onAccept}
            className="h-[92px] w-full max-w-[278px] rounded-[4px] px-10 text-[26px] font-semibold text-white transition hover:brightness-110 disabled:cursor-default disabled:hover:brightness-100"
            style={{ backgroundColor: PURPLE }}
          >
            Accept
          </button>
        )}
      </div>
    </Band>
  );
}

/**
 * One agreement, rolled up.
 *
 * COLLAPSED IS NOT OMITTED: it is in the snapshot and the hash, one click
 * away. Built on <details>, so it opens without JavaScript, is keyboard
 * operable for free, and a browser's find-in-page and the print
 * stylesheet can both reach inside it. The open state is mirrored only
 * to give the heading Qwilr's open styling.
 */
function Agreement({ section }: { section: Section }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const blocks = blocksOf(section);
  return (
    <details
      className="overflow-hidden rounded-[8px] bg-white"
      style={{ border: `1px solid ${HAIRLINE}` }}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary
        className="flex cursor-pointer list-none items-center py-2 pr-4 [&::-webkit-details-marker]:hidden"
        style={{ backgroundColor: open ? TOTAL_GREY : '#FFFFFF' }}
      >
        <span className="flex w-12 shrink-0 justify-center" aria-hidden="true">
          <span className="inline-block text-[13px]" style={{ color: PURPLE }}>
            {open ? '▼' : '▶'}
          </span>
        </span>
        <span className="text-[18px] leading-[30px] sm:text-[20px]" style={{ color: SLATE }}>
          {section.heading}
        </span>
      </summary>
      <div className="px-4 pb-4 pt-4 sm:pl-12" style={{ color: SLATE }}>
        {blocks.map((block, i) => {
          if (block.type === 'paragraph') {
            return (
              <p key={i} className="whitespace-pre-line pb-5 text-[17px] leading-[1.5] sm:text-[20px]">
                <Emphasis text={block.text} />
              </p>
            );
          }
          if (block.type === 'subheading') {
            return (
              <p key={i} className="pb-5 text-[17px] font-bold leading-[1.5] sm:text-[20px]">
                <Emphasis text={block.text} />
              </p>
            );
          }
          // Lists in the agreements are drawn in the purple, as published.
          return (
            <div key={i} style={{ color: PURPLE }}>
              <QuoteListView
                block={block}
                className="pb-5 text-[17px] leading-[1.5] sm:text-[20px]"
                itemClassName="mb-[5px]"
              />
            </div>
          );
        })}
      </div>
    </details>
  );
}
